import { toRgba } from '../utils/color';
import generateID from '@/utils/generate-id';
import { IFTTTAnimation } from '../animation/IFTTTAnimation';
import BezierEasing from 'bezier-easing';
import { FrameAnimation } from '../animation/FrameAnimation';
import { SourceAnimation } from '../animation/SourceAnimation';
import { getRuntimeConfigData } from './config-data';

interface AttrsMap {
  children?: AttrsMap[];
  [key: string]:
    | string
    | number
    | boolean
    | HTMLImageElement
    | AttrsMap[]
    | undefined;
}

const _validMultiUnit = (
  dataItem: AttrsMap,
  sourceConfig: {
    isKpa: boolean;
    isFahrenheit: boolean;
  }
) => {
  const { isKpa, isFahrenheit } = sourceConfig;
  const { tagType, renderRule = 'alwaysShow', source = '' } = dataItem;
  if (renderRule != 'hideWhenUnitMismatch') return true;
  const sourceKeys: string[] = [source.toString()];

  if (tagType == 'DataItemText') {
    const children = dataItem.children ?? [];
    children.forEach((item) => {
      sourceKeys.push(item.source?.toString() ?? '');
    });
  }

  // 验证血压单位是否合法
  const invalidText = isKpa ? 'PressureMmhg' : 'PressureKpa';
  if (
    sourceKeys.some(
      (key) => key.includes('healthBlood') && key.includes(invalidText)
    )
  ) {
    return false;
  }

  // 验证气温单位是否合法
  const weatherKeys = [
    'weatherCurrentTemperature',
    'weatherTodayTemperatureMin',
    'weatherTodayTemperatureMax',
    'weatherTomorrowTemperatureMin',
    'weatherTomorrowTemperatureMax'
  ];
  for (let i = 0; i < sourceKeys.length; i++) {
    const sourceKey = sourceKeys[i];

    if (!weatherKeys.some((key) => sourceKey.startsWith(key))) continue;
    if (isFahrenheit) {
      if (!sourceKey.includes('Fahrenheit')) return false;
    } else {
      if (sourceKey.includes('Fahrenheit')) return false;
    }
  }

  return true;
};

const _parseAstToDataItem = (
  node: Ast,
  images: Record<string, HTMLImageElement>,
  sourceConfig: {
    isKpa: boolean;
    isFahrenheit: boolean;
  },
  dataItems?: any
) => {
  const ignoreTypes = [''];
  const tagType = node.tag;
  if (ignoreTypes.includes(tagType)) return;

  const res: AttrsMap = {
    tagType
  };
  if (tagType == 'Image') {
    const src = node.attrsMap.src;
    const imgEl = images[src ?? ''];
    if (imgEl) res['imgEl'] = imgEl;
  }
  const stringKeys = ['string', '_string', '_id'];
  Object.keys(node.attrsMap).forEach((key) => {
    const val = node.attrsMap[key];
    const isNumber =
      /^(-|\+)?\d+(\.\d+)?$/.test(val) && !stringKeys.includes(key);
    if (isNumber) {
      res[key] = Number(val);
    } else if (val === 'true') {
      res[key] = true;
    } else if (val === 'false') {
      res[key] = false;
    } else {
      res[key] = val;
    }
  });
  if (node.children?.length) {
    res.children = [];
    node.children.forEach((item) => {
      const _res = _parseAstToDataItem(item, images, sourceConfig);
      if (_res) res.children?.push(_res);
    });
  }
  // 验证单位
  if (!_validMultiUnit(res, sourceConfig)) return;
  // 增加多视频动画的重复播放次数
  if (tagType == 'Sprite' && dataItems) {
    const spriteGroupKeys = Object.keys(dataItems).filter(
      (key) => key.indexOf('SpriteGroup') !== -1
    );
    if (spriteGroupKeys.length) {
      spriteGroupKeys.forEach((key) => {
        const spriteGroup = dataItems[key];
        if (
          spriteGroup.children?.find(
            (spriteInfo: any) => spriteInfo.ref == node.attrsMap.ref
          )
        ) {
          res.repeatCount = spriteGroup.repeatCount;
        }
      });
    }
  }
  return res;
};
const _parseAstToLayout = (node: Ast) => {
  const tagType = node.tag;
  const { x, y, ref, _id, _hidden, _scale = '1', _alpha = '1', parameter } = node.attrsMap;
  const res: ILayout = {
    tagType: tagType as 'Layout',
    x: Number(x),
    y: Number(y),
    ref: ref,
    _id: _id,
    _scale: Number(_scale),
    _alpha: Number(_alpha)
  };
  if (_hidden == 'true') res._hidden = true;
  if (parameter) res.parameter = parameter;
  return res;
};

export class RendererRuntime {
  // maml运行时resouse解析后的数据
  layouts: ILayout[] = [];
  dataItems: Record<string, DataItem> = {};
  state: Record<string, RuntimeState> = {};
  /**
   * 实际运行时的数据
   */
  configData: Record<string, string | number> = {};
  /**
   * 全部配置数据项
   */
  configDataWhole: Record<string, string | number> = {};

  // 背景色
  bgColor = '#000000';

  wathfaceAst: Ast;
  images: Record<string, HTMLImageElement>;
  videos: Record<string, HTMLVideoElement>;
  renderTheme: WatchRendererOptions['renderTheme'];
  rive: WatchRendererOptions['rive'];

  // 保存rive中文本的名称
  // 这里使用静态方法，方便不同rive实例共享
  static riveTextRunNames: {
    [key: string]: string[];
  } = {};

  // 动画实例
  iftttAnis: IFTTTAnimation[] = [];
  // 视频或序列帧
  frameAnis: FrameAnimation[] = [];
  // 数据源动画
  sourceAni: SourceAnimation | undefined = undefined;

  optionsDirty = false;

  constructor(
    wathfaceAst: Ast,
    images: Record<string, HTMLImageElement>,
    renderTheme: WatchRendererOptions['renderTheme'],
    configData: WatchRendererOptions['configData'],
    videos: Record<string, HTMLVideoElement>,
    rive: RendererRuntime['rive']
  ) {
    this.clear();
    this.wathfaceAst = wathfaceAst;
    this.images = images;
    this.renderTheme = renderTheme;
    this.videos = videos;
    this.rive = rive;

    this.init(wathfaceAst, images, renderTheme, configData);
  }

  clear() {
    this.clearAniData();
    this.clearBaseData();
  }

  clearBaseData() {
    this.dataItems = {};
    this.state = {};
    this.configDataWhole = {};
    this.configData = {};
    this.layouts = [];

    // 清理rive
    this.rive?.rive.stopRendering();
    this.rive = undefined;
  }

  clearAniData() {
    this.frameAnis = [];
    this.sourceAni = undefined;
    this.iftttAnis = [];
  }

  private init(
    wathfaceAst: Ast,
    images: Record<string, HTMLImageElement>,
    renderTheme: WatchRendererOptions['renderTheme'],
    configData: WatchRendererOptions['configData']
  ) {
    this.initConfigData(configData);
    this.initDataItems(wathfaceAst, images, renderTheme);
    this.initLayouts(wathfaceAst, renderTheme);
  }

  initConfigData(
    configData: WatchRendererOptions['configData'],
    unAssociate?: WatchRendererOptions['configData']
  ) {
    this.configDataWhole = getRuntimeConfigData(configData, unAssociate);
  }

  private initLayouts(
    wathfaceAst: Ast,
    renderTheme: WatchRendererOptions['renderTheme']
  ) {
    const { name, type = 'normal' } = renderTheme;
    const themeAst = wathfaceAst.children.find(
      (item) =>
        item.tag == 'Theme' &&
        item.attrsMap.name == name &&
        item.attrsMap.type == type
    );

    // 将生成前景图的逻辑放到生成ast中实现
    // 处理人像表盘背景和前景图
    // if (themeAst?.attrsMap.isPhotoAlbumWatchface == 'true') {
    //   // 添加前景图
    //   const fgRef = themeAst?.attrsMap.fg;
    //   if (fgRef) {
    //     themeAst.children.push({
    //       tag: 'Layout',
    //       attrsMap: {
    //         ref: fgRef,
    //         x: '0',
    //         y: '0',
    //         _id: generateID(),
    //         _name: '人像表盘前景图片'
    //       },
    //       children: []
    //     });
    //   }
    // }

    let layouts =
      themeAst?.children.map((item) => {
        // 如果是lua，则替换为lua的预览图
        const ref = item.attrsMap.ref;
        if (ref.endsWith('.lua')) {
          // 查找lua的file
          const resourceAst = wathfaceAst.children.find(
            (item) => item.tag == 'Resources'
          );
          const fileName = ref.replace('@', '');
          const fileAst = resourceAst?.children.find(
            (item) => item.tag == 'File' && item.attrsMap.name == fileName
          );
          const filePreview = fileAst?.attrsMap.preview;
          if (filePreview) {
            item.attrsMap.ref = filePreview;
          }
        }
        return _parseAstToLayout(item);
      }) ?? [];

    // 过滤没有数据项的值
    layouts = layouts.filter((item) => !!this.dataItems[item.ref]);
    this.layouts = layouts;
    this.bgColor = toRgba(
      themeAst?.attrsMap['bgColor'] ? themeAst?.attrsMap['bgColor'] : '#000000'
    );
  }

  private initDataItems(
    wathfaceAst: Ast,
    images: Record<string, HTMLImageElement>,
    renderTheme: WatchRendererOptions['renderTheme']
  ) {
    // 需要选择具体colorGroup的元素
    const baseResources = ['Image', 'ImageArray'];
    this.dataItems = {};
    const themeColor = renderTheme.themeColor;
    const isColorGroupMode = renderTheme.colorMode == 'colorGroup';
    const isRecolorMode =
      renderTheme.colorMode == 'recolor' && !!renderTheme.themeColor;
    const isKpa = this.configDataWhole['healthBloodPressureUnit'] == 1;
    const isFahrenheit = this.configDataWhole['weatherTemperatureUnit'] == 1;

    wathfaceAst.children
      .find((item: Ast) => item.tag === 'Resources')
      ?.children?.filter((item) => {
        // 如果是colorGroup模式
        if (isColorGroupMode) {
          if (baseResources.includes(item.tag)) {
            const _colorGroup = item.attrsMap.colorGroup;
            return themeColor ? _colorGroup == themeColor : !_colorGroup;
          } else {
            return true;
          }
        }

        // // 如果是recolor模式,过滤掉不支持recolor的元素
        // if (isRecolorMode && !['Widget', 'Slot'].includes(item.tag)) {
        //   const { recolorEnable, supportRecolor } = item.attrsMap;
        //   if (baseResources.includes(item.tag)) {
        //     return true ?? recolorEnable == 'true';
        //   } else {
        //     // 文本不可recolor
        //     return item.tag == 'DataItemText' ? true : supportRecolor == 'true';
        //   }
        // }
        // 如果是recolor模式，不在此判断，在进行绘制时判断是否recolor
        if (isRecolorMode) {
          return true;
        }

        return true;
      })
      .forEach((resourceAst) => {
        const res = _parseAstToDataItem(
          resourceAst,
          images,
          {
            isKpa,
            isFahrenheit
          },
          this.dataItems
        );
        const name = res?.name as string;
        if (name && res) {
          const dataItem = res as unknown as DataItem;
          this.dataItems['@' + name] = dataItem;
        }
      });
  }

  updateImages(data: {
    images?: Record<string, HTMLImageElement>;
    videos?: Record<string, HTMLVideoElement>;
  }) {
    const { images, videos } = data;
    if (images) {
      this.images = images;
      this.initDataItems(this.wathfaceAst, images, this.renderTheme);
    }
    if (videos) {
      this.videos = videos;
    }

    this.optionsDirty = true;
  }

  updateLayoutAttrs(
    layouts: {
      layoutId: string;
      data: Partial<{
        x: number;
        y: number;
        color: string;
        rotation: number;
        w: number;
        h: number;
      }>;
    }[]
  ) {
    layouts.forEach((item) => {
      const { layoutId, data } = item;
      // 首先需要修改坐标的Layout
      const _layouts = this.layouts.filter((item) =>
        item._id.includes(layoutId)
      );
      if (!_layouts.length) {
        const dataItems = Object.values(this.dataItems ?? {});
        dataItems.forEach((dataItem) => {
          dataItem?.children?.forEach((item2) => {
            const _id = (<ILayout>item2)._id ?? '';
            if (_id?.split?.(',')?.some?.((item) => item == layoutId)) {
              _layouts.push(item2 as ILayout);
            }
          });
        });
      }
      _layouts.forEach((layout) => {
        // 更改x y
        const { x, y, w, h, rotation } = data;
        if (layout) {
          if (x != undefined) layout.x = x;
          if (y != undefined) layout.y = y;
        }
        const dataItem = this.dataItems[layout?.ref ?? ''];
        const tag = dataItem?.tagType;
        // 更改w h，仅Widget和非自动布局文本需要更改w h
        if (['Widget', 'DataItemText'].includes(tag)) {
          if (
            tag != 'DataItemText' ||
            !['size_content', '1023'].includes(
              String((<IDataItemText>dataItem).w)
            )
          ) {
            if (w != undefined) (<IWidget>dataItem).w = w;
            if (h != undefined) (<IWidget>dataItem).h = h;
          }
        }
        // 更改rotation
        if (
          [
            'Image',
            'DataItemImageValues',
            'DataItemImageNumber',
            'DataItemText'
          ].includes(tag)
        ) {
          if (rotation != undefined) (<IImage>dataItem).rotation = rotation;
        }
      });
    });

    this.optionsDirty = true;
  }

  static generateStateKey(name: string, layoutId: string) {
    return name + '-' + layoutId;
  }

  resetIftttAnimations2(iftttId: string, aniId: string) {
    this.iftttAnis = [];
    // 查找要播放的动画ast
    const dataItem = Object.values(this.dataItems).find(
      (item) => (item as unknown as IDataItemAnimation)._iftttId == iftttId
    ) as IDataItemAnimation | undefined;
    const { children, _shapeId: shapeId } = dataItem ?? {};
    const ani = children?.find((item) => item._aniId == aniId);

    // 查找具体需要播放动画的ast节点
    // const layout = this.getLayout(shapeId ?? '');
    // const widget = this.getDataItem<IWidget>(layout?.ref ?? '');
    const shapeIds = [shapeId];
    if (!ani || !shapeIds?.length) return;

    const { attr } = ani;
    const _ani = JSON.parse(
      attr.replaceAll('&quot;', '"')
    ) as IDataItemAnimationAttr;

    const { toState, config, fromState } = _ani ?? {};
    const { ease = [], duration: _duration } = config ?? {};
    // config中的单位是s，这里需要改为ms
    // const delay = _delay ? _delay * 1000 : 0;
    const duration = _duration ? _duration * 1000 : 300;

    const { scale = 1, rotate = 0, x, y, opacity: toOpacity = 1 } = toState;
    const { opacity = 1 } = fromState ?? {};

    let type: IFTTTAnimation['type'] | undefined;
    const aniValues: IFTTTAnimation['toState'] = {};
    const fromValues: IFTTTAnimation['fromState'] = {};
    if (x || y) {
      type = 'position';
      aniValues.x = x ?? 0;
      aniValues.y = y ?? 0;
    }
    if (opacity < 1 || toOpacity < 1) {
      type = 'alpha';
      fromValues.alpha = opacity;
      aniValues.toAlpha = toOpacity;
    }
    if (rotate) {
      type = 'rotation';
      aniValues.rotation = rotate;
      // aniValues.pivotX = pivotX;
      // aniValues.pivotY = pivotY;
    }
    if (scale != 1) {
      type = 'scale';
      aniValues.scale = scale;
      // aniValues.pivotX = pivotX;
      // aniValues.pivotY = pivotY;
    }

    if (!type) return;

    const easePoints =
      ease?.length >= 2 ? ease?.splice(2) : ['0.25', '0.1', '0.25', '0.1'];

    const params: ConstructorParameters<typeof IFTTTAnimation>[2] = {
      duration,
      easing: BezierEasing(
        Number(easePoints[0] ?? 0.25),
        Number(easePoints[1] ?? 0.1),
        Number(easePoints[2] ?? 0.25),
        Number(easePoints[3] ?? 0.1)
      ),
      delay: 0,
      toState: aniValues,
      fromState: fromValues
    };

    shapeIds.forEach((shapeId) => {
      if (!shapeId) return;
      const aniIns = this.iftttAnis.find(
        (item) => item.shapeId == shapeId && item.type == type
      );
      if (aniIns) {
        aniIns.reset(params);
      } else {
        this.iftttAnis.push(new IFTTTAnimation(shapeId, type, params));
      }
    });
  }

  resetFrameAnimation(shapeIds: string[]) {
    this.frameAnis = [];
    shapeIds.forEach((shapeId) => {
      const layout = this.getLayout(shapeId);
      const sprite = this.getDataItem<ISprite>(layout?.ref ?? '');

      if (!layout || !sprite) return;
      const file = this.getDataItem<IFile>(
        sprite.refExt ? sprite.refExt : sprite?.ref ?? ''
      );
      const type = file?.tagType == 'File' ? 'video' : 'sprite';
      let videoEl: HTMLVideoElement | undefined;
      if (type == 'video') {
        videoEl = this.videos?.[file?.fileName ?? ''];
      }

      this.frameAnis.push(
        new FrameAnimation({
          shapeId,
          type,
          videoEl,
          repeatCount: sprite.repeatCount ?? 1,
          frameInterval: sprite.interval ?? 17
        })
      );
    });
  }

  resetSourceAnimation(data: ConstructorParameters<typeof SourceAnimation>[0]) {
    if (!data.length) this.sourceAni = undefined;
    this.sourceAni = new SourceAnimation(data);
  }

  static generateSizeName(name: string, layoutId: string) {
    return name + '-' + layoutId;
  }

  getDataItem<T>(ref: string) {
    return this.dataItems[ref] as T | undefined;
  }
  getLayout(shapeId: string) {
    const _layouts: ILayout[] = [];
    // 递归查询
    const _get = (layouts: ILayout[]) => {
      layouts.forEach((layout) => {
        _layouts.push(layout);
        const dataitem = this.getDataItem<IWidget | ISlot>(layout.ref);
        if (dataitem?.children?.length) {
          _get(dataitem.children);
        }
      });
    };
    _get(this.layouts);
    return _layouts.find((item) => item._id == shapeId);
  }
}
